import { TranscarentSsoControllerService } from "@9amhealth/openapi";
import React, { FC, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import FunnelKey from "src/constants/funnelKey";
import envVariables from "src/lib/envVariables";
import {
  claimOneTimeToken,
  releaseOneTimeToken
} from "src/lib/oneTimeTokenGuard";
import { SAML_FUNNEL_KEY_SESSION_STORAGE_KEY } from "src/lib/useSamlAuth";
import { authenticationState } from "src/state/state";
import { NiceError } from "src/ui/styled/NiceError";
import Loader from "../Loader/Loader";
import Translate from "../Translate/Translate";
import useUserAuthStage from "src/state/hooks/useUserAuthStage";
import { reportAuthError } from "src/lib/reportAuthError";
import { handleAuthSuccess } from "./handleAuthSuccess";
import { useBlocNext } from "@blac/react";
import AppViewCubit from "src/state/AppViewCubit/AppViewCubit";
import { isSpentTokenError, parseExchangeToken } from "./parseExchangeToken";

const HandleOneTimeToken: FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = React.useState<string | null>(null);
  const [response, setResponse] = React.useState<unknown | null>(null);
  const token = searchParams.get("token") ?? searchParams.get("lookupKey");
  const funnel = FunnelKey.transcarent;
  const isProd = envVariables.APP_ENV === "production";
  const { authStage } = useUserAuthStage();
  const navigate = useNavigate();
  const [finishedHandover, setFinishedHandover] = React.useState(false);
  const [, appViewState] = useBlocNext(AppViewCubit);
  const hasReportedLoginFailure = useRef(false);

  useEffect(() => {
    appViewState.setPartnerSession(true);
  }, [appViewState]);

  const exchangeTokenDetails = useMemo(
    () => parseExchangeToken(token),
    [token]
  );

  useEffect(() => {
    if (exchangeTokenDetails.status === "ok") {
      return;
    }

    const message =
      exchangeTokenDetails.status === "invalid"
        ? "Invalid Exchange Token"
        : "Missing Exchange Token";

    setError(message);
    reportAuthError("ott", new Error(message), { hasToken: Boolean(token) });
  }, [exchangeTokenDetails, token]);

  useEffect(() => {
    if (exchangeTokenDetails.status !== "ok") {
      return;
    }

    const { token: exchangeToken, userId } = exchangeTokenDetails;

    // Claim before prepareForExternalTokenLogin: a reload or a Back button
    // mounts a fresh tree and would otherwise re-send a consumed token.
    if (!claimOneTimeToken("ott", exchangeToken)) {
      // The token stays in the URL after a failure, so landing here again is
      // routine. Send an authenticated member on, and tell everyone else the
      // token is spent — never leave them watching the loader.
      if (authenticationState.state.authenticationStatus === "authenticated") {
        try {
          handleAuthSuccess(navigate);
        } catch (err: unknown) {
          reportAuthError("ott", err, { userId, step: "navigate" });
        }
      } else {
        setError("Exchange token already used");
      }
      return;
    }

    // Prepare app for external exchange to avoid stale sessions
    authenticationState.prepareForExternalTokenLogin();
    sessionStorage.setItem(SAML_FUNNEL_KEY_SESSION_STORAGE_KEY, funnel);

    void TranscarentSsoControllerService.exchangeToken({
      token: exchangeToken
    })
      .then((res) => {
        authenticationState.externalLoginInProgress = false;

        void authenticationState
          .setCredentials(
            {
              accessToken: res.data.accessToken,
              refreshToken: res.data.refreshToken
            },
            { deferBootstrap: true }
          )
          .then(() => {
            setFinishedHandover(true);
            try {
              handleAuthSuccess(navigate);
            } catch (err: unknown) {
              // Credentials are already saved, so a navigation failure must not
              // be reported to the member as a failed login.
              reportAuthError("ott", err, { userId, step: "navigate" });
            }
          })
          .catch((err: unknown) => {
            setFinishedHandover(true);
            setError("Could not set credentials");
            reportAuthError("ott", err, { userId, step: "setCredentials" });
          });
      })
      .catch((err: unknown) => {
        authenticationState.externalLoginInProgress = false;
        // The backend may never have seen the token, so let it be retried.
        releaseOneTimeToken("ott", exchangeToken);
        setResponse(err);
        if (err instanceof Error) {
          setError(err.message);
        }
        // Reported here, once, rather than from an effect keyed on `error` —
        // `error` is never cleared, so that effect re-fired on every authStage
        // transition and turned one failed exchange into several Sentry issues.
        reportAuthError(
          "ott",
          err,
          { userId, hasToken: true, step: "exchangeToken" },
          { expected: isSpentTokenError(err) }
        );
      });
  }, [exchangeTokenDetails, funnel, navigate]);

  useEffect(() => {
    if (
      (authStage === "noAuth" || authStage === "nullAuth") &&
      finishedHandover &&
      !hasReportedLoginFailure.current
    ) {
      hasReportedLoginFailure.current = true;
      // Log failed login attempt
      reportAuthError("ott", new Error("HandleOneTimeToken login failed"), {
        authStage,
        hasToken: Boolean(token)
      });
    }
  }, [authStage, finishedHandover, token]);

  if (error) {
    return (
      <NiceError>
        <h1>
          <Translate msg="auth.ott.error" />
        </h1>
        {!isProd && (
          <>
            <em>error:</em>
            <pre>{error}</pre>
            <em>response:</em>
            <pre>{JSON.stringify(response, null, 2)}</pre>
          </>
        )}
      </NiceError>
    );
  }

  if (
    (authStage === "noAuth" || authStage === "nullAuth") &&
    finishedHandover
  ) {
    return (
      <NiceError>
        <h1>
          <Translate msg="auth.loginFailed" />
        </h1>
      </NiceError>
    );
  }

  return <Loader active fixed absolute fullPage bg="partner" />;
};

export default HandleOneTimeToken;
